import { apiService } from './api.service';
import { Conversation, Message, SendMessageRequest, PageResponse } from '../types';

class MessageService {
    /**
     * Get all conversations for the current user
     */
    async getConversations(): Promise<Conversation[]> {
        const response = await apiService.get<Conversation[]>('conversations');
        // Backend returns array directly, not a PageResponse
        return response.data || [];
    }

    /**
     * Get a specific conversation by ID
     */
    async getConversation(id: string): Promise<Conversation> {
        const response = await apiService.get<Conversation>(`conversations/${id}`);
        return response.data;
    }

    /**
     * Get messages for a conversation
     */
    async getMessages(conversationId: string, page = 0, size = 50): Promise<PageResponse<Message>> {
        const response = await apiService.get<PageResponse<Message>>(
            `conversations/${conversationId}/messages`,
            { page, size }
        );
        return response.data;
    }

    /**
     * Send a message in a conversation
     */
    async sendMessage(request: SendMessageRequest): Promise<Message> {
        const response = await apiService.post<Message>(
            `conversations/${request.conversationId}/messages`,
            { text: request.content }
        );
        return response.data;
    }

    /**
     * Mark all messages in a conversation as read
     */
    async markAsRead(conversationId: string): Promise<void> {
        await apiService.post(`conversations/${conversationId}/read`);
    }

    /**
     * Start a new conversation with a provider (optionally about a job)
     */
    async startConversation(providerId: string, jobId?: string): Promise<Conversation> {
        const response = await apiService.post<Conversation>('conversations', {
            providerId,
            jobId
        });
        return response.data;
    }

    /**
     * Get unread message count
     */
    async getUnreadCount(): Promise<number> {
        const response = await apiService.get<{ count: number }>('conversations/unread-count');
        return response.data?.count || 0;
    }
}

export const messageService = new MessageService();
